import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Plus } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";

export const Manifesto = () => {
  const { t } = useLanguage();
  const chapters = t("manifesto.chapters") || [];
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" className="relative py-24 md:py-40" data-testid="manifesto">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-16 lg:gap-x-16">
          {/* Left sticky heading */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <span className="font-sans uppercase tracking-[0.25em] text-xs font-semibold text-primary">
                  {t("manifesto.overline")}
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-foreground text-balance">
                  {t("manifesto.title")}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 font-sans text-sm md:text-base leading-relaxed font-light text-muted-foreground max-w-md" data-testid="about-spiritism">
                  {t("manifesto.spiritism")}
                </p>
              </Reveal>

              <figure ref={imgRef} className="mt-12 hidden lg:block">
                <div className="relative overflow-hidden rounded-sm aspect-[4/5]">
                  <motion.img
                    style={{ y, scale: 1.15 }}
                    src="/images/estesia-house.png"
                    alt="Estesia Bern — Steinerstrasse 14"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 shadow-[inset_0_0_120px_rgba(0,0,0,0.35)]" />
                </div>
                <figcaption className="mt-3 font-sans text-[11px] leading-snug tracking-wide text-muted-foreground/90 whitespace-nowrap text-right" data-testid="about-caption">
                  {t("manifesto.caption")}
                  <span className="uppercase tracking-[0.18em] text-[9px] text-primary/80 ml-1.5 align-middle">↲</span>
                </figcaption>
              </figure>
            </div>
          </div>

          {/* Right numbered chapters */}
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="flex flex-col">
              {chapters.map((c, i) => (
                <Reveal
                  key={c.n}
                  delay={i * 0.06}
                  className={`grid grid-cols-[auto_1fr] gap-6 md:gap-10 py-10 md:py-14 ${
                    i !== 0 ? "border-t border-border" : ""
                  }`}
                >
                  <span className="font-serif text-5xl md:text-7xl leading-none text-primary/30">
                    {c.n}
                  </span>
                  <div className={i % 2 === 1 ? "md:pl-10" : ""}>
                    <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-3">{c.title}</h3>
                    <p className="font-sans text-base leading-relaxed font-light text-muted-foreground max-w-md">
                      {c.body}
                    </p>
                    {c.n === "02" && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <button
                            type="button"
                            data-testid="origin-readmore"
                            className="mt-5 inline-flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.18em] text-primary hover:text-primary/70 transition-colors"
                          >
                            <Plus size={12} strokeWidth={2} />
                            {t("manifesto.readmore")}
                          </button>
                        </DialogTrigger>
                        <DialogContent
                          data-testid="origin-modal"
                          className="max-w-2xl max-h-[85vh] overflow-y-auto bg-background text-foreground p-8 md:p-10"
                        >
                          <DialogTitle className="font-serif text-2xl md:text-3xl text-foreground pr-8">
                            {t("manifesto.originStoryTitle")}
                          </DialogTitle>
                          <DialogDescription className="sr-only">
                            {t("manifesto.originStoryTitle")}
                          </DialogDescription>
                          <div className="mt-4 font-sans text-[15px] leading-relaxed text-muted-foreground whitespace-pre-line">
                            {t("manifesto.originStoryBody")}
                          </div>
                          <p className="mt-6 pt-4 border-t border-border font-sans text-[11px] uppercase tracking-[0.2em] font-semibold text-primary">
                            {t("manifesto.originStorySignature")}
                          </p>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 md:mt-28 border-t border-border pt-14" data-testid="manifesto-quote">
          <blockquote className="max-w-4xl">
            <p className="font-serif italic text-2xl sm:text-3xl md:text-4xl leading-[1.3] text-foreground text-balance">
              “{t("manifesto.quote")}”
            </p>
            <footer className="mt-6 font-sans uppercase tracking-[0.2em] text-xs font-semibold text-primary">
              {t("manifesto.quoteAuthor")}
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  );
};
